import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import {
  collection,
  collectionGroup,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  setDoc,
  orderBy,
  deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage, handleFirestoreError, OperationType } from './firebase';
import { Container, Item } from './types';
import { analyzeItemImage, analyzeContainerContents, SuggestedItem } from './services/geminiService';
import {
  Package,
  Plus,
  QrCode,
  Search,
  Camera,
  Trash2,
  LogOut,
  ChevronRight,
  Box,
  MapPin,
  Loader2,
  X,
  Image as ImageIcon,
  ArrowLeft,
  Smartphone,
  Pencil,
  CheckCircle2,
  AlertCircle,
  Wifi,
  Users,
  Copy,
  Link2,
  Link2Off,
  FileDown,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ─── Utilities ────────────────────────────────────────────────────────────────

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

async function uploadImage(file: File, userId: string): Promise<string> {
  const filename = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
  const storageRef = ref(storage, `users/${userId}/items/${filename}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
}

/** Short human-readable tote ID. New containers get a generated shortId; legacy ones fall back to their Firebase ID prefix. */
function getToteId(container: Container): string {
  return container.shortId ?? container.id.slice(0, 5).toUpperCase();
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [containers, setContainers] = useState<Container[]>([]);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [view, setView] = useState<'dashboard' | 'container' | 'scan'>('dashboard');

  // Modals / overlays
  const [isAddingContainer, setIsAddingContainer] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingContainer, setEditingContainer] = useState<Container | null>(null);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [showLabelPreview, setShowLabelPreview] = useState<Container | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Family
  const [linkedUserId, setLinkedUserId] = useState<string | null>(null);
  const [linkedUserProfile, setLinkedUserProfile] = useState<{ displayName?: string; email: string } | null>(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);

  // Print
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState<Set<string>>(new Set());

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState<Array<{ item: Item; container: Container }>>([]);
  const [searchingItems, setSearchingItems] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // NFC
  const [nfcReading, setNfcReading] = useState(false);
  const nfcReaderRef = useRef<any>(null);

  // Recently accessed containers (persisted in localStorage)
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('recentContainers') || '[]'); } catch { return []; }
  });

  // Export
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Container detail filter
  const [containerFilter, setContainerFilter] = useState('');

  // Expanded item in container view
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // Collapsed category sections in container view
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const confirm = (message: string, onConfirm: () => void) => {
    setConfirmDialog({ message, onConfirm });
  };

  const markRecent = (id: string) => {
    setRecentIds(prev => {
      const next = [id, ...prev.filter(x => x !== id)].slice(0, 4);
      localStorage.setItem('recentContainers', JSON.stringify(next));
      return next;
    });
  };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  // ── Auth ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            createdAt: serverTimestamp()
          });
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // ── User profile listener (tracks linkedUserId) ───────────────────────────────

  useEffect(() => {
    if (!user) { setLinkedUserId(null); setLinkedUserProfile(null); return; }
    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), async (snap) => {
      const partnerId = snap.data()?.linkedUserId || null;
      setLinkedUserId(partnerId);
      if (partnerId) {
        const partnerSnap = await getDoc(doc(db, 'users', partnerId));
        if (partnerSnap.exists()) {
          setLinkedUserProfile({ displayName: partnerSnap.data().displayName, email: partnerSnap.data().email });
        }
      } else {
        setLinkedUserProfile(null);
      }
    });
    return () => unsubscribe();
  }, [user]);

  // ── Containers listener ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    const ownerIds = linkedUserId ? [user.uid, linkedUserId] : [user.uid];
    const q = query(
      collection(db, 'containers'),
      where('ownerId', 'in', ownerIds),
      orderBy('updatedAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setContainers(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Container)));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'containers'));
    return () => unsubscribe();
  }, [user, linkedUserId]);

  // ── Items listener ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedContainer) { setItems([]); return; }
    const q = query(
      collection(db, `containers/${selectedContainer.id}/items`),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Item)));
    }, (error) => handleFirestoreError(error, OperationType.LIST, `containers/${selectedContainer.id}/items`));
    return () => unsubscribe();
  }, [selectedContainer]);

  // ── Global item search (debounced) ────────────────────────────────────────────

  useEffect(() => {
    if (!user || searchQuery.trim().length < 2) {
      setItemSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingItems(true);
      try {
        const ownerIds = linkedUserId ? [user.uid, linkedUserId] : [user.uid];
        const q = query(collectionGroup(db, 'items'), where('ownerId', 'in', ownerIds));
        const snapshot = await getDocs(q);
        const lower = searchQuery.toLowerCase();
        const results: Array<{ item: Item; container: Container }> = [];
        for (const d of snapshot.docs) {
          const item = { id: d.id, ...d.data() } as Item;
          const matches =
            item.name.toLowerCase().includes(lower) ||
            (item.description || '').toLowerCase().includes(lower) ||
            (item.tags || []).some(t => t.toLowerCase().includes(lower));
          if (matches) {
            const container = containers.find(c => c.id === item.containerId);
            if (container) results.push({ item, container });
          }
        }
        setItemSearchResults(results);
      } catch (e) {
        console.error('Item search error:', e);
      }
      setSearchingItems(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [user, searchQuery, containers, linkedUserId]);

  // ── Container CRUD ────────────────────────────────────────────────────────────

  const createContainer = async (name: string, location: string, description: string, suggestedItems?: SuggestedItem[]) => {
    if (!user) return;
    try {
      const shortId = Math.random().toString(36).substring(2, 7).toUpperCase();
      const containerRef = await addDoc(collection(db, 'containers'), {
        ownerId: user.uid, name, location, description, shortId,
        itemCount: 0, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      });
      if (suggestedItems && suggestedItems.length > 0) {
        for (const item of suggestedItems) {
          await addDoc(collection(db, `containers/${containerRef.id}/items`), {
            ...item, containerId: containerRef.id, ownerId: user.uid, quantity: 1, createdAt: serverTimestamp()
          });
        }
        await updateDoc(containerRef, { itemCount: suggestedItems.length, updatedAt: serverTimestamp() });
      }
      setIsAddingContainer(false);
      showToast(suggestedItems?.length ? `Container created with ${suggestedItems.length} items` : 'Container created');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'containers');
      showToast('Failed to create container', 'error');
    }
  };

  const updateContainer = async (id: string, name: string, location: string, description: string) => {
    try {
      await updateDoc(doc(db, 'containers', id), { name, location, description, updatedAt: serverTimestamp() });
      setEditingContainer(null);
      // Keep selectedContainer in sync
      if (selectedContainer?.id === id) {
        setSelectedContainer(prev => prev ? { ...prev, name, location, description } : null);
      }
      showToast('Container updated');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `containers/${id}`);
      showToast('Failed to update container', 'error');
    }
  };

  const deleteContainer = (id: string, name: string) => {
    confirm(`Delete "${name}" and all its items? This cannot be undone.`, async () => {
      try {
        const itemsSnap = await getDocs(collection(db, `containers/${id}/items`));
        await Promise.all(itemsSnap.docs.map(d => deleteDoc(d.ref)));
        await deleteDoc(doc(db, 'containers', id));
        if (selectedContainer?.id === id) { setSelectedContainer(null); setView('dashboard'); }
        showToast('Container deleted');
      } catch (error) {
        handleFirestoreError(error, OperationType.DELETE, `containers/${id}`);
        showToast('Failed to delete container', 'error');
      }
    });
  };

  // ── Family linking ────────────────────────────────────────────────────────────

  const linkFamily = async (partnerUid: string): Promise<string | null> => {
    if (!user) return 'Not logged in.';
    if (partnerUid === user.uid) return 'That is your own code.';
    try {
      const partnerSnap = await getDoc(doc(db, 'users', partnerUid));
      if (!partnerSnap.exists()) return 'No account found with that code.';
      // Set my linkedUserId to partner
      await updateDoc(doc(db, 'users', user.uid), { linkedUserId: partnerUid });
      // Set partner's linkedUserId to me (rules allow this specific operation)
      await updateDoc(doc(db, 'users', partnerUid), { linkedUserId: user.uid });
      return null;
    } catch (error) {
      return 'Failed to link. Make sure the code is correct.';
    }
  };

  const unlinkFamily = async () => {
    if (!user) return;
    try {
      if (linkedUserId) {
        await updateDoc(doc(db, 'users', linkedUserId), { linkedUserId: deleteField() });
      }
      await updateDoc(doc(db, 'users', user.uid), { linkedUserId: deleteField() });
      showToast('Family account unlinked.');
    } catch (error) {
      showToast('Failed to unlink.', 'error');
    }
  };

  // ── Item CRUD ─────────────────────────────────────────────────────────────────

  const addItem = async (containerId: string, itemData: Partial<Item>) => {
    if (!user) return;
    try {
      await addDoc(collection(db, `containers/${containerId}/items`), {
        ...itemData, containerId, ownerId: user.uid, createdAt: serverTimestamp()
      });
      const containerRef = doc(db, 'containers', containerId);
      const snap = await getDoc(containerRef);
      if (snap.exists()) {
        await updateDoc(containerRef, { itemCount: (snap.data().itemCount || 0) + 1, updatedAt: serverTimestamp() });
      }
      setIsAddingItem(false);
      showToast('Item added');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `containers/${containerId}/items`);
      showToast('Failed to add item', 'error');
    }
  };

  const updateItem = async (containerId: string, itemId: string, data: Partial<Item>) => {
    try {
      await updateDoc(doc(db, `containers/${containerId}/items`, itemId), data);
      setEditingItem(null);
      showToast('Item updated');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `containers/${containerId}/items/${itemId}`);
      showToast('Failed to update item', 'error');
    }
  };

  const deleteItem = (containerId: string, itemId: string, itemName: string) => {
    confirm(`Delete "${itemName}"?`, async () => {
      try {
        await deleteDoc(doc(db, `containers/${containerId}/items`, itemId));
        const containerRef = doc(db, 'containers', containerId);
        const snap = await getDoc(containerRef);
        if (snap.exists()) {
          await updateDoc(containerRef, {
            itemCount: Math.max(0, (snap.data().itemCount || 1) - 1),
            updatedAt: serverTimestamp()
          });
        }
        showToast('Item deleted');
      } catch (error) {
        handleFirestoreError(error, OperationType.DELETE, `containers/${containerId}/items/${itemId}`);
        showToast('Failed to delete item', 'error');
      }
    });
  };

  // ── NFC ───────────────────────────────────────────────────────────────────────

  const writeNfcTag = async (containerId: string) => {
    if (!('NDEFReader' in window)) {
      showToast('NFC is not supported on this device', 'error');
      return;
    }
    try {
      const ndef = new (window as any).NDEFReader();
      await ndef.write({ records: [{ recordType: 'text', data: `tt:${containerId}` }] });
      await updateDoc(doc(db, 'containers', containerId), {
        nfcTagId: containerId, updatedAt: serverTimestamp()
      });
      if (selectedContainer?.id === containerId) {
        setSelectedContainer(prev => prev ? { ...prev, nfcTagId: containerId } : null);
      }
      showToast('NFC tag written successfully');
    } catch (err: any) {
      showToast(err.message || 'NFC write failed', 'error');
    }
  };

  const startNfcScan = async () => {
    if (!('NDEFReader' in window)) {
      showToast('NFC is not supported on this device (Android Chrome only)', 'error');
      return;
    }
    if (nfcReading) return;
    try {
      setNfcReading(true);
      const ndef = new (window as any).NDEFReader();
      nfcReaderRef.current = ndef;
      await ndef.scan();
      ndef.addEventListener('reading', ({ message }: any) => {
        for (const record of message.records) {
          if (record.recordType === 'text') {
            const decoder = new TextDecoder(record.encoding || 'utf-8');
            const text = decoder.decode(record.data);
            if (text.startsWith('tt:')) {
              const id = text.replace('tt:', '');
              const container = containers.find(c => c.id === id);
              if (container) {
                setSelectedContainer(container);
                setView('container');
                setNfcReading(false);
              } else {
                showToast('Container not found', 'error');
              }
              return;
            }
          }
        }
        showToast('Unrecognized NFC tag', 'error');
      });
      ndef.addEventListener('readingerror', () => {
        showToast('Could not read NFC tag', 'error');
        setNfcReading(false);
      });
    } catch (err: any) {
      showToast(err.message || 'NFC scan failed', 'error');
      setNfcReading(false);
    }
  };

  const stopNfcScan = () => {
    nfcReaderRef.current = null;
    setNfcReading(false);
  };

  // ── Export ────────────────────────────────────────────────────────────────────

  const exportCSV = async () => {
    showToast('Preparing CSV…');
    try {
      const rows: string[][] = [
        ['Tote ID', 'Container', 'Location', 'Description', 'Item Name', 'Qty', 'Tags', 'Item Description'],
      ];
      for (const c of containers) {
        const snap = await getDocs(collection(db, `containers/${c.id}/items`));
        if (snap.empty) {
          rows.push([getToteId(c), c.name, c.location ?? '', c.description ?? '', '', '', '', '']);
        } else {
          for (const d of snap.docs) {
            const item = d.data() as Item;
            rows.push([
              getToteId(c), c.name, c.location ?? '', c.description ?? '',
              item.name, String(item.quantity),
              (item.tags ?? []).join('; '),
              item.description ?? '',
            ]);
          }
        }
      }
      const csv = rows.map(r => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `totetracker-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV downloaded');
    } catch {
      showToast('Export failed', 'error');
    }
  };

  const exportPDF = async () => {
    showToast('Preparing PDF…');
    try {
      type ContainerExport = { container: Container; items: Item[] };
      const data: ContainerExport[] = [];
      for (const c of containers) {
        const snap = await getDocs(collection(db, `containers/${c.id}/items`));
        data.push({ container: c, items: snap.docs.map(d => ({ id: d.id, ...d.data() } as Item)) });
      }
      const totalItems = data.reduce((s, d) => s + d.items.length, 0);
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
<title>ToteTracker Export — ${date}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:system-ui,-apple-system,Arial,sans-serif;color:#111;background:#fff;font-size:13px}
  .page-header{padding:28px 32px 20px;border-bottom:3px solid #111;display:flex;align-items:flex-end;justify-content:space-between}
  .page-header h1{font-size:22px;font-weight:900;letter-spacing:-0.5px}
  .page-header p{color:#666;font-size:12px;margin-top:3px}
  .stats{font-size:12px;color:#666;text-align:right}
  .tote{padding:20px 32px;border-bottom:1px solid #e5e7eb;page-break-inside:avoid}
  .tote-header{display:flex;align-items:flex-start;gap:12px;margin-bottom:12px}
  .tote-id{background:#111;color:#fff;padding:3px 9px;border-radius:5px;font-size:12px;font-weight:700;font-family:monospace;flex-shrink:0;margin-top:2px}
  .tote-name{font-size:17px;font-weight:700;line-height:1.2}
  .tote-meta{color:#666;font-size:12px;margin-top:3px}
  table{width:100%;border-collapse:collapse}
  thead th{text-align:left;padding:7px 10px;background:#f4f4f5;border-bottom:1px solid #e4e4e7;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#555}
  tbody td{padding:7px 10px;border-bottom:1px solid #f4f4f5;vertical-align:top;line-height:1.4}
  tbody tr:last-child td{border-bottom:none}
  .qty{display:inline-block;background:#f4f4f5;padding:1px 7px;border-radius:999px;font-size:11px;font-weight:600}
  .tag{display:inline-block;background:#e4e4e7;padding:1px 6px;border-radius:4px;font-size:10px;margin:1px 2px 1px 0}
  .empty{color:#aaa;font-style:italic;padding:8px 10px;font-size:12px}
  .page-footer{padding:16px 32px;color:#aaa;font-size:11px;border-top:1px solid #e5e7eb;margin-top:8px}
  @media print{
    .tote{page-break-inside:avoid}
    .page-footer{position:fixed;bottom:0;width:100%}
  }
</style></head><body>
<div class="page-header">
  <div>
    <h1>ToteTracker</h1>
    <p>Inventory export · ${escHtml(user!.displayName ?? user!.email ?? '')}</p>
  </div>
  <div class="stats">${data.length} container${data.length !== 1 ? 's' : ''} · ${totalItems} item${totalItems !== 1 ? 's' : ''}<br>${date}</div>
</div>
${data.map(({ container: c, items }) => `
<div class="tote">
  <div class="tote-header">
    <span class="tote-id">${escHtml(getToteId(c))}</span>
    <div>
      <div class="tote-name">${escHtml(c.name)}</div>
      ${[c.location, c.description].filter(Boolean).map(s => `<div class="tote-meta">${escHtml(s!)}</div>`).join('')}
    </div>
  </div>
  ${items.length === 0
    ? '<p class="empty">No items in this container</p>'
    : `<table>
    <thead><tr><th>Item</th><th>Qty</th><th>Tags</th><th>Notes</th></tr></thead>
    <tbody>
      ${items.map(item => `<tr>
        <td>${escHtml(item.name)}</td>
        <td><span class="qty">×${item.quantity}</span></td>
        <td>${(item.tags ?? []).map(t => `<span class="tag">#${escHtml(t)}</span>`).join('')}</td>
        <td>${escHtml(item.description ?? '')}</td>
      </tr>`).join('')}
    </tbody></table>`}
</div>`).join('')}
<div class="page-footer">Generated by ToteTracker · ${date}</div>
<script>window.addEventListener('load',()=>setTimeout(()=>window.print(),400))<\/script>
</body></html>`;

      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) { alert('Pop-up blocked — please allow pop-ups for this page and try again.'); return; }
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch {
      showToast('Export failed', 'error');
    }
  };

  // ── Print ─────────────────────────────────────────────────────────────────────

  const togglePrintSelection = (id: string) => {
    const next = new Set(selectedForPrint);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedForPrint(next);
  };

  const downloadQRCode = (containerId: string, containerName: string) => {
    const svg = document.getElementById(`qr-${containerId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width; canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `QR-${containerName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  // ── Auth handlers ─────────────────────────────────────────────────────────────

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      console.error('Login failed', error);
    }
  };

  const handleLogout = () => signOut(auth);

  // ── Loading / Login screens ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center space-y-8"
        >
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-zinc-900 rounded-3xl flex items-center justify-center shadow-xl">
              <Package className="w-10 h-10 text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight text-zinc-900">ToteTracker</h1>
            <p className="text-zinc-500">Smart storage management with QR codes and AI.</p>
          </div>
          <button
            onClick={handleLogin}
            className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-semibold shadow-lg hover:bg-zinc-800 transition-all flex items-center justify-center gap-3"
          >
            <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
            Sign in with Google
          </button>
        </motion.div>
      </div>
    );
  }

  // ── Main App ──────────────────────────────────────────────────────────────────

  const filteredContainers = containers.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredItems = items.filter(item =>
    !containerFilter ||
    item.name.toLowerCase().includes(containerFilter.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(containerFilter.toLowerCase()) ||
    (item.tags || []).some(t => t.toLowerCase().includes(containerFilter.toLowerCase()))
  );

  const itemsByCategory = filteredItems.reduce<Record<string, Item[]>>((acc: Record<string, Item[]>, item: Item) => {
    const cat = item.category || 'Uncategorized';
    (acc[cat] = acc[cat] || []).push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-zinc-200 px-3 sm:px-6 py-3 flex items-center justify-between">
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer"
          onClick={() => { setView('dashboard'); setSelectedContainer(null); stopNfcScan(); }}
        >
          <Package className="w-6 h-6 text-zinc-900 flex-shrink-0" />
          <span className="font-bold text-lg sm:text-xl tracking-tight">ToteTracker</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setShowFamilyModal(true)}
            className={`p-3 rounded-xl transition-colors ${linkedUserId ? 'text-blue-500 hover:bg-blue-50 active:bg-blue-100' : 'text-zinc-400 hover:bg-zinc-100 active:bg-zinc-200'}`}
            title={linkedUserId ? `Family: ${linkedUserProfile?.displayName || linkedUserProfile?.email || 'linked'}` : 'Set up family account'}
          >
            <Users className="w-5 h-5" />
          </button>
          <button
            onClick={handleLogout}
            className="p-3 hover:bg-zinc-100 active:bg-zinc-200 rounded-xl transition-colors text-zinc-400 hover:text-red-500"
          >
            <LogOut className="w-5 h-5" />
          </button>
          <img src={user.photoURL || ''} className="w-8 h-8 rounded-full border border-zinc-200 flex-shrink-0" alt="Profile" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-3 sm:p-6">
        <AnimatePresence mode="wait">

          {/* ── Dashboard ── */}
          {view === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
              className="space-y-8"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Your Containers</h2>
                  <p className="text-zinc-500 text-sm sm:text-base">Manage your storage units and totes.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {containers.length > 0 && (
                    <button
                      onClick={() => setIsPrintingLabels(!isPrintingLabels)}
                      className={cn(
                        'flex-1 sm:flex-none px-4 py-3 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2 active:scale-95',
                        isPrintingLabels ? 'bg-zinc-200 text-zinc-900' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                      )}
                    >
                      <QrCode className="w-5 h-5 flex-shrink-0" />
                      <span>{isPrintingLabels ? 'Cancel' : 'Print Labels'}</span>
                    </button>
                  )}
                  <button
                    onClick={() => setView('scan')}
                    className="flex-1 sm:flex-none bg-white border border-zinc-200 text-zinc-700 px-4 py-3 rounded-2xl font-semibold hover:bg-zinc-50 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <QrCode className="w-5 h-5 flex-shrink-0" /> <span>Scan QR</span>
                  </button>
                  {/* Export dropdown */}
                  {containers.length > 0 && (
                    <div className="relative flex-1 sm:flex-none">
                      <button
                        onClick={() => setShowExportMenu((v: boolean) => !v)}
                        className="w-full sm:w-auto bg-white border border-zinc-200 text-zinc-700 px-4 py-3 rounded-2xl font-semibold hover:bg-zinc-50 active:scale-95 transition-all flex items-center justify-center gap-2"
                      >
                        <FileDown className="w-5 h-5 flex-shrink-0" /> <span>Export</span>
                      </button>
                      {showExportMenu && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                          <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-zinc-200 rounded-2xl shadow-xl overflow-hidden min-w-[180px]">
                            <button
                              onClick={() => { setShowExportMenu(false); exportPDF(); }}
                              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left"
                            >
                              <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                              <div>
                                <p className="font-semibold text-sm">Export PDF</p>
                                <p className="text-xs text-zinc-400">Full inventory report</p>
                              </div>
                            </button>
                            <div className="border-t border-zinc-100" />
                            <button
                              onClick={() => { setShowExportMenu(false); exportCSV(); }}
                              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left"
                            >
                              <FileSpreadsheet className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                              <div>
                                <p className="font-semibold text-sm">Export CSV</p>
                                <p className="text-xs text-zinc-400">For Excel / Sheets</p>
                              </div>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => setIsAddingContainer(true)}
                    className="flex-1 sm:flex-none bg-zinc-900 text-white px-4 py-3 rounded-2xl font-semibold shadow-md hover:bg-zinc-800 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5 flex-shrink-0" /> <span>New Container</span>
                  </button>
                </div>
              </div>

              {isPrintingLabels && selectedForPrint.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900 text-white p-4 rounded-2xl flex items-center justify-between gap-3 shadow-xl"
                >
                  <span className="font-medium text-sm">{selectedForPrint.size} selected</span>
                  <button
                    onClick={() => window.print()}
                    className="bg-white text-zinc-900 px-4 py-2 rounded-xl font-bold text-sm hover:bg-zinc-100 active:scale-95 transition-all flex-shrink-0"
                  >
                    Print Labels
                  </button>
                </motion.div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search containers and items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white border border-zinc-200 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 transition-all"
                />
                {searchingItems && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-400" />
                )}
              </div>

              {/* Item search results */}
              {searchQuery.trim().length >= 2 && itemSearchResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400">Items Found</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {itemSearchResults.map(({ item, container }) => (
                      <div
                        key={item.id}
                        onClick={() => { markRecent(container.id); setSelectedContainer(container); setView('container'); setSearchQuery(''); }}
                        className="bg-white border border-zinc-200 p-3 rounded-2xl flex items-center gap-3 cursor-pointer hover:border-zinc-900/20 hover:shadow-md transition-all"
                      >
                        <div className="w-10 h-10 bg-zinc-100 rounded-xl overflow-hidden flex-shrink-0">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} className="w-full h-full object-cover" alt={item.name} referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-300">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{item.name}</p>
                          <p className="text-xs text-zinc-400 truncate">in {container.name}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recently accessed */}
              {recentIds.length > 0 && !searchQuery && !isPrintingLabels && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400">Recently Accessed</h3>
                  <div className="flex gap-2 flex-wrap">
                    {recentIds.map(id => containers.find(c => c.id === id)).filter(Boolean).map(c => (
                      <button
                        key={c!.id}
                        onClick={() => { markRecent(c!.id); setSelectedContainer(c!); setView('container'); }}
                        className="bg-white border border-zinc-200 rounded-2xl px-4 py-2 text-sm font-semibold hover:border-zinc-900/20 hover:shadow-md transition-all flex items-center gap-2"
                      >
                        <Box className="w-4 h-4 text-zinc-400" />{c!.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Container grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredContainers.map(container => (
                  <motion.div
                    layoutId={container.id}
                    key={container.id}
                    onClick={() => {
                      if (isPrintingLabels) togglePrintSelection(container.id);
                      else { markRecent(container.id); setSelectedContainer(container); setView('container'); }
                    }}
                    className={cn(
                      'group bg-white border p-5 sm:p-6 rounded-3xl shadow-sm transition-all cursor-pointer relative overflow-hidden active:scale-[0.98]',
                      isPrintingLabels && selectedForPrint.has(container.id)
                        ? 'border-zinc-900 ring-2 ring-zinc-900/10'
                        : container.itemCount === 0
                          ? 'border-dashed border-zinc-200 hover:shadow-lg hover:border-zinc-900/20'
                          : 'border-zinc-200 hover:shadow-xl hover:border-zinc-900/20'
                    )}
                  >
                    {isPrintingLabels && (
                      <div className={cn(
                        'absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
                        selectedForPrint.has(container.id) ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-200'
                      )}>
                        {selectedForPrint.has(container.id) && <Plus className="w-4 h-4 text-white rotate-45" />}
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-4">
                      <div className={cn(
                        'w-12 h-12 rounded-2xl flex items-center justify-center group-hover:bg-zinc-900 group-hover:text-white transition-colors',
                        container.itemCount === 0 ? 'bg-zinc-50 text-zinc-300' : 'bg-zinc-100 text-zinc-900'
                      )}>
                        <Box className="w-6 h-6" />
                      </div>
                      {!isPrintingLabels && (
                        <div className="flex gap-0.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingContainer(container); }}
                            className="p-2.5 text-zinc-400 hover:text-zinc-700 active:text-zinc-900 transition-colors rounded-xl hover:bg-zinc-50 active:bg-zinc-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteContainer(container.id, container.name); }}
                            className="p-2.5 text-zinc-400 hover:text-red-500 active:text-red-600 transition-colors rounded-xl hover:bg-zinc-50 active:bg-zinc-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-1">{container.name}</h3>
                    <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      {container.location || 'No location set'}
                    </div>
                    {container.nfcTagId && (
                      <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium mb-2">
                        <Wifi className="w-3 h-3" /> NFC Tagged
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-zinc-100 mt-3">
                      <div className="flex items-center gap-1.5">
                        <span className={cn('text-sm font-bold', container.itemCount === 0 ? 'text-zinc-300' : 'text-zinc-900')}>
                          {container.itemCount}
                        </span>
                        <span className="text-sm text-zinc-400">{container.itemCount === 1 ? 'item' : 'items'}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-zinc-900 transition-colors" />
                    </div>
                  </motion.div>
                ))}
              </div>

              {containers.length === 0 && !isAddingContainer && (
                <div className="text-center py-20 border-2 border-dashed border-zinc-200 rounded-3xl">
                  <Package className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-zinc-900">No containers yet</h3>
                  <p className="text-zinc-500 mb-6">Start by creating your first storage tote.</p>
                  <button
                    onClick={() => setIsAddingContainer(true)}
                    className="text-zinc-900 font-semibold hover:underline"
                  >
                    Create a container
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* ── Container view ── */}
          {view === 'container' && selectedContainer && (
            <motion.div
              key="container-view"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="space-y-3 mb-2">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => { setView('dashboard'); setSelectedContainer(null); }}
                    className="p-3 hover:bg-zinc-100 active:bg-zinc-200 rounded-xl transition-colors flex-shrink-0"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{selectedContainer.name}</h2>
                    {selectedContainer.location && (
                      <div className="flex items-center gap-1 text-zinc-500 text-sm mt-1">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> {selectedContainer.location}
                      </div>
                    )}
                    {selectedContainer.description && (
                      <p className="text-sm text-zinc-400 mt-1">{selectedContainer.description}</p>
                    )}
                    {selectedContainer.nfcTagId && (
                      <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium mt-1">
                        <Wifi className="w-3 h-3" /> NFC Tagged
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pl-1">
                  <button
                    onClick={() => setEditingContainer(selectedContainer)}
                    className="p-3 bg-white border border-zinc-200 rounded-2xl hover:bg-zinc-50 active:bg-zinc-100 transition-all"
                    title="Edit Container"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowLabelPreview(selectedContainer)}
                    className="p-3 bg-white border border-zinc-200 rounded-2xl hover:bg-zinc-50 active:bg-zinc-100 transition-all"
                    title="Label / QR Code"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsAddingItem(true)}
                    className="flex-1 sm:flex-none bg-zinc-900 text-white px-5 py-3 rounded-2xl font-semibold shadow-md hover:bg-zinc-800 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" /> Add Item
                  </button>
                </div>
              </div>

              {/* Filter bar */}
              {items.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Filter items..."
                    value={containerFilter}
                    onChange={e => setContainerFilter(e.target.value)}
                    className="w-full bg-white border border-zinc-200 rounded-2xl py-3 pl-11 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                  />
                  {containerFilter && (
                    <button onClick={() => setContainerFilter('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-zinc-100 active:bg-zinc-200 rounded-full transition-colors">
                      <X className="w-4 h-4 text-zinc-400" />
                    </button>
                  )}
                </div>
              )}

              {/* Items by category */}
              <div className="space-y-4">
                {Object.entries(itemsByCategory).map(([category, catItems]) => (
                  <div key={category}>
                    {Object.keys(itemsByCategory).length > 1 && (
                      <button
                        onClick={() => toggleCategory(category)}
                        className="flex items-center gap-2 w-full text-left mb-2 py-1"
                      >
                        <ChevronRight className={cn('w-4 h-4 text-zinc-400 transition-transform duration-200', !collapsedCategories.has(category) && 'rotate-90')} />
                        <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">{category}</span>
                        <span className="text-xs text-zinc-300 ml-auto font-medium">{catItems.length}</span>
                      </button>
                    )}
                    {!collapsedCategories.has(category) && (
                      <div className="space-y-2">
                        {catItems.map(item => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
                          >
                            {/* Compact row — always visible */}
                            <div
                              className="flex items-center gap-3 p-3 cursor-pointer select-none"
                              onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                            >
                              <div className="w-12 h-12 bg-zinc-100 rounded-xl overflow-hidden flex-shrink-0">
                                {item.imageUrl ? (
                                  <img src={item.imageUrl} className="w-full h-full object-cover" alt={item.name} referrerPolicy="no-referrer" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-zinc-300">
                                    <ImageIcon className="w-5 h-5" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm truncate">{item.name}</h4>
                                {item.description && expandedItem !== item.id && (
                                  <p className="text-xs text-zinc-400 truncate">{item.description}</p>
                                )}
                              </div>
                              <span className="text-xs bg-zinc-100 px-2 py-1 rounded-full text-zinc-500 flex-shrink-0 font-medium">×{item.quantity}</span>
                              {/* Always-visible action buttons */}
                              <div className="flex gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={() => setEditingItem(item)}
                                  className="p-2.5 text-zinc-400 hover:text-zinc-700 active:text-zinc-900 transition-colors rounded-xl hover:bg-zinc-50 active:bg-zinc-100"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteItem(selectedContainer.id, item.id, item.name)}
                                  className="p-2.5 text-zinc-400 hover:text-red-500 active:text-red-600 transition-colors rounded-xl hover:bg-zinc-50 active:bg-zinc-100"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {/* Expanded detail */}
                            <AnimatePresence>
                              {expandedItem === item.id && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-3 pb-3 pt-2 border-t border-zinc-100 space-y-2">
                                    {item.description && (
                                      <p className="text-sm text-zinc-500">{item.description}</p>
                                    )}
                                    {item.tags && item.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {item.tags.map(tag => (
                                          <span key={tag} className="text-xs bg-zinc-100 px-2 py-0.5 rounded-full text-zinc-500 font-medium">#{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {filteredItems.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-100 rounded-2xl">
                    <p className="text-zinc-400">{containerFilter ? 'No items match your filter.' : 'This container is empty. Add your first item!'}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ── Scan view ── */}
          {view === 'scan' && (
            <motion.div
              key="scan-view"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="max-w-md mx-auto space-y-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold">Scan to Open</h2>
                  <p className="text-sm text-zinc-500 mt-0.5">Point your camera at a ToteTracker QR code to jump straight to that container.</p>
                </div>
                <button onClick={() => { setView('dashboard'); stopNfcScan(); }} className="p-2 hover:bg-zinc-100 rounded-full flex-shrink-0 mt-0.5">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* QR Scanner */}
              <div className="bg-white border border-zinc-200 rounded-3xl overflow-hidden">
                <div className="px-5 pt-5 pb-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <QrCode className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">QR Code</p>
                    <p className="text-xs text-zinc-400">Hold steady inside the frame</p>
                  </div>
                </div>
                <Scanner onScan={(id) => {
                  const container = containers.find(c => c.id === id);
                  if (container) { markRecent(id); setSelectedContainer(container); setView('container'); }
                  else showToast('Container not found', 'error');
                }} />
              </div>

              {/* NFC Scanner */}
              <div className="bg-white border border-zinc-200 rounded-3xl p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-zinc-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">NFC Tag</p>
                    <p className="text-xs text-zinc-400">Tap your phone to a ToteTracker NFC tag</p>
                  </div>
                  <span className="ml-auto text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Android Chrome</span>
                </div>
                {nfcReading ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-zinc-600 bg-zinc-50 rounded-2xl py-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Hold tag to back of phone...
                    </div>
                    <button onClick={stopNfcScan} className="w-full text-xs text-red-500 font-semibold py-2 hover:bg-red-50 rounded-xl transition-colors">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={startNfcScan}
                    className="w-full px-5 py-2.5 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 transition-all"
                  >
                    Scan NFC Tag
                  </button>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      {/* ── Modals ── */}
      <Modal isOpen={isAddingContainer} onClose={() => setIsAddingContainer(false)} title="New Container">
        <ContainerForm onSubmit={createContainer} onCancel={() => setIsAddingContainer(false)} />
      </Modal>

      <Modal isOpen={!!editingContainer} onClose={() => setEditingContainer(null)} title="Edit Container">
        {editingContainer && (
          <ContainerForm
            initial={editingContainer}
            onSubmit={(name, loc, desc) => updateContainer(editingContainer.id, name, loc, desc)}
            onCancel={() => setEditingContainer(null)}
            submitLabel="Save Changes"
          />
        )}
      </Modal>

      <Modal isOpen={isAddingItem} onClose={() => setIsAddingItem(false)} title="Add Item">
        <ItemForm
          userId={user.uid}
          onSubmit={(data) => addItem(selectedContainer!.id, data)}
          onCancel={() => setIsAddingItem(false)}
        />
      </Modal>

      <Modal isOpen={!!editingItem} onClose={() => setEditingItem(null)} title="Edit Item">
        {editingItem && selectedContainer && (
          <ItemForm
            userId={user.uid}
            initial={editingItem}
            onSubmit={(data) => updateItem(selectedContainer.id, editingItem.id, data)}
            onCancel={() => setEditingItem(null)}
            submitLabel="Save Changes"
          />
        )}
      </Modal>

      <Modal isOpen={!!showLabelPreview} onClose={() => setShowLabelPreview(null)} title="Label & QR Code">
        {showLabelPreview && (
          <LabelPreview
            container={showLabelPreview}
            onDownload={() => downloadQRCode(showLabelPreview.id, showLabelPreview.name)}
            onPrint={() => window.print()}
            onWriteNfc={() => writeNfcTag(showLabelPreview.id)}
          />
        )}
      </Modal>

      <Modal isOpen={showFamilyModal} onClose={() => setShowFamilyModal(false)} title="Family Account">
        <FamilyForm
          myUid={user.uid}
          linkedUserId={linkedUserId}
          linkedUserProfile={linkedUserProfile}
          onLink={linkFamily}
          onUnlink={unlinkFamily}
          onClose={() => setShowFamilyModal(false)}
        />
      </Modal>

      <ConfirmModal
        dialog={confirmDialog}
        onClose={() => setConfirmDialog(null)}
      />

      {/* ── Print sections ── */}
      <div className="print-section">
        {selectedForPrint.size > 0 && (
          <div className="grid grid-cols-2 gap-8 p-8 bg-white">
            {containers.filter(c => selectedForPrint.has(c.id)).map(c => (
              <div key={c.id} className="border-2 border-zinc-900 p-6 rounded-3xl text-center space-y-4 break-inside-avoid bg-white">
                <h2 className="text-2xl font-bold">{c.name}</h2>
                <p className="text-zinc-500">{c.location}</p>
                <div className="flex justify-center">
                  <QRCodeSVG value={`tt:${c.id}`} size={160} />
                </div>
                <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">ToteTracker • {c.id.slice(0, 8)}</p>
              </div>
            ))}
          </div>
        )}
        {showLabelPreview && (
          <div className="flex items-center justify-center min-h-screen bg-white">
            <div className="border-4 border-zinc-900 p-12 rounded-[3rem] text-center space-y-6 bg-white max-w-md w-full">
              <h1 className="text-5xl font-bold">{showLabelPreview.name}</h1>
              <p className="text-2xl text-zinc-500">{showLabelPreview.location}</p>
              <div className="flex justify-center p-4">
                <QRCodeSVG value={`tt:${showLabelPreview.id}`} size={300} />
              </div>
              <p className="text-sm font-mono text-zinc-400 uppercase tracking-[0.2em]">ToteTracker • {showLabelPreview.id.slice(0, 8)}</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Toast ── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className={cn(
              'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-2xl shadow-xl font-medium text-sm max-w-[90vw] text-center',
              toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-zinc-900 text-white'
            )}
          >
            {toast.type === 'error'
              ? <AlertCircle className="w-4 h-4 flex-shrink-0" />
              : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            }
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        onClick={onClose}
        className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="p-3 hover:bg-zinc-100 active:bg-zinc-200 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 sm:p-6 overflow-y-auto">{children}</div>
      </motion.div>
    </div>
  );
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────

function ConfirmModal({ dialog, onClose }: {
  dialog: { message: string; onConfirm: () => void } | null;
  onClose: () => void;
}) {
  if (!dialog) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        onClick={onClose}
        className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 space-y-5"
      >
        <p className="text-zinc-700 font-medium leading-relaxed">{dialog.message}</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 font-semibold text-zinc-500 hover:bg-zinc-50 rounded-xl transition-colors">
            Cancel
          </button>
          <button
            onClick={() => { dialog.onConfirm(); onClose(); }}
            className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── FamilyForm ───────────────────────────────────────────────────────────────

function FamilyForm({ myUid, linkedUserId, linkedUserProfile, onLink, onUnlink, onClose }: {
  myUid: string;
  linkedUserId: string | null;
  linkedUserProfile: { displayName?: string; email: string } | null;
  onLink: (partnerUid: string) => Promise<string | null>;
  onUnlink: () => Promise<void>;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(myUid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const err = await onLink(code.trim());
    setLoading(false);
    if (err) setError(err);
    else setCode('');
  };

  return (
    <div className="space-y-6">
      {linkedUserId ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900">
                {linkedUserProfile?.displayName || linkedUserProfile?.email || 'Family member'}
              </p>
              {linkedUserProfile?.displayName && (
                <p className="text-xs text-zinc-400 truncate">{linkedUserProfile.email}</p>
              )}
            </div>
            <Link2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
          </div>
          <p className="text-sm text-zinc-500 text-center">You're sharing all totes with this person. Anything either of you adds is visible to both.</p>
          <button
            onClick={async () => { await onUnlink(); onClose(); }}
            className="w-full py-3 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Link2Off className="w-4 h-4" /> Unlink family account
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-zinc-500">Link your account with your partner so you both see the same totes. Share your code with them, then enter theirs.</p>

          <div className="space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Your family code</p>
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-xl p-3">
              <code className="flex-1 text-sm font-mono text-zinc-700 truncate">{myUid}</code>
              <button
                onClick={copyCode}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors rounded-lg hover:bg-zinc-100 flex-shrink-0"
              >
                {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-zinc-400">Share this code with your partner.</p>
          </div>

          <form onSubmit={handleLink} className="space-y-3">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Enter partner's code</p>
              <input
                type="text"
                autoFocus
                required
                placeholder="Paste their family code here"
                value={code}
                onChange={e => setCode(e.target.value)}
                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-zinc-900 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Link accounts
            </button>
          </form>
        </div>
      )}

      <button onClick={onClose} className="w-full py-2 text-sm font-semibold text-zinc-400 hover:bg-zinc-50 rounded-xl transition-colors">
        Done
      </button>
    </div>
  );
}

// ─── ContainerForm (create + edit) ───────────────────────────────────────────

function ContainerForm({ onSubmit, onCancel, initial, submitLabel = 'Create Container' }: {
  onSubmit: (name: string, location: string, description: string, suggestedItems?: SuggestedItem[]) => void;
  onCancel: () => void;
  initial?: { name: string; location?: string; description?: string };
  submitLabel?: string;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [location, setLocation] = useState(initial?.location || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [suggestedItems, setSuggestedItems] = useState<SuggestedItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzeError(null);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      setAnalyzing(true);
      try {
        const result = await analyzeContainerContents(base64);
        if (result?.items?.length) {
          setSuggestedItems(result.items);
        } else {
          setAnalyzeError('No items detected — try a clearer photo with better lighting.');
        }
      } catch {
        setAnalyzeError('Analysis failed. Check your connection and try again.');
      }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  const removeSuggested = (index: number) =>
    setSuggestedItems(items => items.filter((_, i) => i !== index));

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSubmit(name, location, description, suggestedItems.length ? suggestedItems : undefined); }}>
      <div className="space-y-1">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Container Name</label>
        <input
          autoFocus required value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Holiday Decorations"
          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Location</label>
        <input
          value={location} onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. Attic, Shelf A"
          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Description</label>
        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="What's inside?"
          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 h-20 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
        />
      </div>

      {/* AI photo scan */}
      {!initial && (
        <div className="space-y-3">
          {analyzing ? (
            <div className="w-full flex flex-col items-center justify-center gap-2 py-5 bg-zinc-900 rounded-xl text-white">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm font-semibold">Analyzing photo...</span>
              <span className="text-xs text-zinc-400">AI is identifying items in your tote</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setAnalyzeError(null); fileInputRef.current?.click(); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-zinc-200 rounded-xl text-sm font-semibold text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 transition-all"
            >
              <Camera className="w-4 h-4" />{analyzeError ? 'Try again' : 'Scan tote contents with AI'}
            </button>
          )}
          <input
            type="file" ref={fileInputRef} className="hidden"
            accept="image/*" capture="environment"
            onChange={handleImageChange}
          />

          {analyzeError && (
            <p className="text-sm text-red-500 text-center">{analyzeError}</p>
          )}

          {suggestedItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                AI-detected items ({suggestedItems.length}) — tap × to remove
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {suggestedItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 bg-zinc-50 rounded-xl px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-800 truncate">{item.name}</p>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {item.tags.map(tag => (
                          <span key={tag} className="text-[10px] bg-zinc-200 px-1.5 py-0.5 rounded font-bold text-zinc-500">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeSuggested(i)} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0 mt-0.5">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 py-3 font-semibold text-zinc-500 hover:bg-zinc-50 rounded-xl transition-colors">Cancel</button>
        <button type="submit" disabled={analyzing} className="flex-1 py-3 bg-zinc-900 text-white font-semibold rounded-xl shadow-lg hover:bg-zinc-800 transition-all disabled:opacity-50">{submitLabel}</button>
      </div>
    </form>
  );
}

// ─── ItemForm (create + edit) ─────────────────────────────────────────────────

function ItemForm({ onSubmit, onCancel, userId, initial, submitLabel = 'Add Item' }: {
  onSubmit: (data: Partial<Item>) => void;
  onCancel: () => void;
  userId: string;
  initial?: Item;
  submitLabel?: string;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [quantity, setQuantity] = useState(initial?.quantity || 1);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initial?.imageUrl || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tags, setTags] = useState<string[]>(initial?.tags || []);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      setImagePreview(base64);
      setAnalyzing(true);
      const analysis = await analyzeItemImage(base64.split(',')[1]);
      if (analysis) {
        if (!initial) { // only auto-fill on new items
          setName(analysis.name || '');
          setDescription(analysis.description || '');
          setTags(analysis.tags || []);
        }
      }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    setUploadError(null);
    try {
      let imageUrl: string | undefined = initial?.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile, userId);
      }
      const itemData: Partial<Item> = { name, description, quantity, tags };
      if (imageUrl !== undefined) itemData.imageUrl = imageUrl;
      onSubmit(itemData);
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeTag = (tag: string) => setTags(tags.filter(t => t !== tag));
  const addTag = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) setTags([...tags, trimmed]);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Image picker */}
      <div className="flex justify-center">
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-32 h-32 bg-zinc-50 border-2 border-dashed border-zinc-200 rounded-3xl flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-100 transition-all relative overflow-hidden"
        >
          {imagePreview ? (
            <img src={imagePreview} className="w-full h-full object-cover" alt="Preview" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-zinc-300 mb-2" />
              <span className="text-[10px] font-bold uppercase text-zinc-400">Add Photo</span>
            </>
          )}
          {(analyzing || uploading) && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-1">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-900" />
              <span className="text-[10px] font-bold text-zinc-600">{analyzing ? 'Analyzing...' : 'Uploading...'}</span>
            </div>
          )}
        </div>
        {/* capture="environment" opens rear camera directly on mobile */}
        <input
          type="file" ref={fileInputRef} className="hidden"
          accept="image/*" capture="environment"
          onChange={handleImageChange}
        />
      </div>

      <div className="space-y-1">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Item Name</label>
        <input
          required value={name} onChange={(e) => setName(e.target.value)}
          placeholder="What is it?"
          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Quantity</label>
          <input
            type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Tags</label>
          <div className="flex flex-wrap gap-1 p-2 bg-zinc-50 border border-zinc-200 rounded-xl min-h-[46px]">
            {tags.map(tag => (
              <span key={tag} className="text-[10px] bg-zinc-200 px-2 py-1 rounded-md font-bold text-zinc-600 flex items-center gap-1">
                {tag}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
              </span>
            ))}
            <input
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
              placeholder={tags.length === 0 ? 'Add tag...' : ''}
              className="bg-transparent text-xs outline-none flex-1 min-w-[50px]"
            />
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Description</label>
        <textarea
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional details..."
          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 h-20 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
        />
      </div>

      {uploadError && <p className="text-sm text-red-500 text-center">{uploadError}</p>}
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 py-3 font-semibold text-zinc-500 hover:bg-zinc-50 rounded-xl transition-colors">Cancel</button>
        <button
          type="submit" disabled={uploading || analyzing}
          className="flex-1 py-3 bg-zinc-900 text-white font-semibold rounded-xl shadow-lg hover:bg-zinc-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

// ─── Dymo label printing ──────────────────────────────────────────────────────

const DYMO_SIZES = [
  { id: '30252', label: 'Address — 1⅛" × 3½"',              w: 89,    h: 28,   nameSize: '7.5pt', locSize: '5.5pt' },
  { id: '30256', label: 'Large shipping — 4" × 2¼" (30256)', w: 101.6, h: 57.1, nameSize: '14pt',  locSize: '10pt'  },
  { id: '30336', label: 'Multi-purpose — 1" × 2⅛"',          w: 54,    h: 25,   nameSize: '6.5pt', locSize: '5pt'   },
  { id: '30334', label: 'Multi-purpose — 2¼" × 1¼"',         w: 57,    h: 32,   nameSize: '9pt',   locSize: '7pt'   },
  { id: '30321', label: 'Shipping — 2 5/16" × 4"',           w: 101,   h: 59,   nameSize: '16pt',  locSize: '11pt'  },
  { id: '30370', label: 'Return address — ¾" × 2"',          w: 51,    h: 19,   nameSize: '5pt',   locSize: '4pt'   },
] as const;

type DymoSize = typeof DYMO_SIZES[number];

function printDymoLabel(container: Container, size: DymoSize) {
  const svgEl = document.getElementById(`qr-${container.id}`);
  if (!svgEl) return;

  const svgString = new XMLSerializer().serializeToString(svgEl);
  const qrDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);

  // For landscape labels (w > h), swap the @page dimensions and rotate layout
  const isLandscape = size.w > size.h;
  // @page size is always given as portrait (short × long); orientation keyword handles rotation
  const pageW = isLandscape ? size.h : size.w;
  const pageH = isLandscape ? size.w : size.h;
  const orientation = isLandscape ? 'landscape' : 'portrait';
  const qrMm = size.h - 6;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  @page {
    size: ${pageW}mm ${pageH}mm ${orientation};
    margin: 0;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html {
    width: ${size.w}mm;
    height: ${size.h}mm;
    max-height: ${size.h}mm;
    overflow: hidden;
  }
  body {
    width: ${size.w}mm;
    height: ${size.h}mm;
    max-height: ${size.h}mm;
    overflow: hidden;
    background: white;
    display: flex;
    align-items: center;
    padding: 2mm 2mm 2mm 7mm;
    font-family: Arial, Helvetica, sans-serif;
    page-break-after: avoid;
    break-after: avoid;
  }
  .qr  { width: ${qrMm}mm; height: ${qrMm}mm; flex-shrink: 0; display: block; }
  .txt { padding-left: 2.5mm; flex: 1; overflow: hidden; max-width: ${size.w - qrMm - 8}mm; }
  .name { font-size: ${size.nameSize}; font-weight: bold; line-height: 1.2; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .loc  { font-size: ${size.locSize}; color: #444; margin-top: 0.8mm; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .tid  { font-size: ${size.locSize}; color: #888; margin-top: 0.5mm; font-family: monospace; }
</style>
</head><body>
  <img class="qr" src="${qrDataUrl}" />
  <div class="txt">
    <div class="name">${container.name}</div>
    ${container.location ? `<div class="loc">${container.location}</div>` : ''}
    <div class="tid">#${getToteId(container)}</div>
  </div>
  <script>window.addEventListener('load', () => setTimeout(() => { window.print(); }, 300));<\/script>
</body></html>`;

  const win = window.open('', '_blank', 'width=500,height=300');
  if (!win) {
    alert('Pop-up blocked — please allow pop-ups for this page and try again.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// ─── LabelPreview ─────────────────────────────────────────────────────────────

function LabelPreview({ container, onDownload, onPrint, onWriteNfc }: {
  container: Container;
  onDownload: () => void;
  onPrint: () => void;
  onWriteNfc: () => void;
}) {
  const nfcSupported = 'NDEFReader' in window;
  const [selectedSize, setSelectedSize] = useState<DymoSize>(
    () => DYMO_SIZES.find(s => s.id === localStorage.getItem('defaultLabelSize')) ?? DYMO_SIZES[0]
  );
  const [step, setStep] = useState<'size' | 'preview'>(
    localStorage.getItem('defaultLabelSize') ? 'preview' : 'size'
  );

  // Scale the live preview to ~280px wide, preserving label aspect ratio
  const PREVIEW_W = 280;
  const previewH = Math.round(PREVIEW_W * (selectedSize.h / selectedSize.w));
  const qrPx = previewH - 8;

  return (
    <div className="space-y-5">

      {/* ── Step 1: Size picker ── */}
      {step === 'size' && (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold">Choose Label Size</h4>
            <p className="text-sm text-zinc-500 mt-0.5">Pick the Dymo label stock you have loaded.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {DYMO_SIZES.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  localStorage.setItem('defaultLabelSize', s.id);
                  setSelectedSize(s);
                }}
                className={cn(
                  'p-3 rounded-2xl border-2 text-left transition-all',
                  selectedSize.id === s.id ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-100 hover:border-zinc-300'
                )}
              >
                <div className="mb-2 flex items-center justify-center h-10">
                  <div
                    style={{
                      width: Math.round(40 * (s.w / Math.max(s.w, s.h))),
                      height: Math.round(40 * (s.h / Math.max(s.w, s.h)))
                    }}
                    className="bg-zinc-200 rounded border border-zinc-300"
                  />
                </div>
                <p className="text-xs font-bold leading-tight">{s.label.split(' — ')[0]}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">{s.label.split(' — ')[1]}</p>
              </button>
            ))}
          </div>
          <button
            onClick={() => setStep('preview')}
            className="w-full py-3 bg-zinc-900 text-white font-semibold rounded-xl hover:bg-zinc-800 transition-all"
          >
            Continue
          </button>
        </div>
      )}

      {/* ── Step 2: Preview + actions ── */}
      {step === 'preview' && (
        <div className="space-y-5">
          {/* ── Label Maker section ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">Label Maker Print</p>
              <button
                onClick={() => setStep('size')}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" /> Change size
              </button>
            </div>

            {/* Live label preview */}
            <div className="flex justify-center bg-zinc-50 rounded-2xl py-5">
              <div
                style={{ width: PREVIEW_W, height: previewH }}
                className="bg-white border-2 border-zinc-900 rounded flex items-center px-3 py-1 shadow"
              >
                <QRCodeSVG value={`tt:${container.id}`} size={qrPx} />
                <div className="ml-2 overflow-hidden flex-1 min-w-0">
                  <p className="font-bold leading-tight text-[11px] truncate">{container.name}</p>
                  {container.location && (
                    <p className="text-zinc-500 leading-tight text-[9px] truncate mt-0.5">{container.location}</p>
                  )}
                  <p className="text-zinc-400 leading-tight text-[9px] mt-0.5 font-mono">#{getToteId(container)}</p>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-zinc-400 text-center -mt-1">
              {selectedSize.label} — proportional preview
            </p>

            <button
              onClick={() => printDymoLabel(container, selectedSize)}
              className="w-full py-3 bg-zinc-900 text-white font-semibold rounded-xl shadow-lg hover:bg-zinc-800 transition-all flex items-center justify-center gap-2"
            >
              <QrCode className="w-4 h-4" />
              Print to Label Maker
            </button>
            <div className="text-[10px] text-zinc-400 space-y-1 leading-relaxed">
              <p>A new tab will open and trigger your print dialog automatically.</p>
              <p>In the dialog: select your <strong>Dymo LabelWriter</strong> — the page size is pre-set.</p>
              <p className="text-amber-600 font-medium">⚠ Use Chrome for best results.</p>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-5 space-y-3">
            <p className="text-sm font-bold">Full Sheet / PNG</p>

            {/* Full-size QR label — used for sheet printing and PNG download */}
            <div className="border-2 border-zinc-900 p-6 rounded-3xl text-center space-y-3 bg-white">
              <div className="flex items-center justify-center gap-2">
                <h2 className="text-2xl font-bold">{container.name}</h2>
                <span className="bg-zinc-900 text-white text-xs font-mono font-bold px-2 py-0.5 rounded-md">#{getToteId(container)}</span>
              </div>
              <p className="text-zinc-500 text-sm">{container.location || 'No location'}</p>
              <div className="flex justify-center py-2">
                <QRCodeSVG id={`qr-${container.id}`} value={`tt:${container.id}`} size={200} />
              </div>
              <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">ToteTracker</p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={onDownload}
                className="py-3 bg-zinc-100 text-zinc-900 font-semibold rounded-xl hover:bg-zinc-200 transition-all text-sm"
              >
                Download
              </button>
              <button
                onClick={onPrint}
                className="py-3 bg-zinc-900 text-white font-semibold rounded-xl shadow-lg hover:bg-zinc-800 transition-all text-sm"
              >
                Print Sheet
              </button>
              <button
                onClick={onWriteNfc}
                disabled={!nfcSupported}
                title={nfcSupported ? 'Write NFC tag' : 'NFC not supported on this device'}
                className={cn(
                  'py-3 font-semibold rounded-xl text-sm flex items-center justify-center gap-1.5 transition-all',
                  nfcSupported
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg'
                    : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                )}
              >
                <Wifi className="w-4 h-4" />
                {container.nfcTagId ? 'Re-write' : 'NFC'}
              </button>
            </div>

            {container.nfcTagId && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                NFC tag assigned to this container.
              </div>
            )}
          </div>

          <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl">
            <p className="text-xs text-amber-800 font-medium flex items-center gap-1.5 mb-1">
              <Smartphone className="w-3 h-3" /> Trouble printing?
            </p>
            <p className="text-[10px] text-amber-700 leading-relaxed">
              Browsers may block printing inside an iframe. Click the <strong>open in new tab</strong> icon and try again there.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Scanner ──────────────────────────────────────────────────────────────────

function Scanner({ onScan }: { onScan: (id: string) => void }) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const onScanRef = useRef(onScan);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  onScanRef.current = onScan;

  const startCamera = () => {
    setStarted(true);
    // Use requestAnimationFrame to ensure the reader div is rendered at full size before starting
    requestAnimationFrame(() => {
      const scanner = new Html5Qrcode('reader');
      scannerRef.current = scanner;
      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText: string) => {
          if (decodedText.startsWith('tt:')) {
            scanner.stop().then(() => {
              scannerRef.current = null;
              onScanRef.current(decodedText.replace('tt:', ''));
            }).catch(() => {
              scannerRef.current = null;
              onScanRef.current(decodedText.replace('tt:', ''));
            });
          }
        },
        () => {}
      ).catch((err: any) => {
        setStarted(false);
        setError(err?.message || 'Camera access denied. Check your browser permissions.');
      });
    });
  };

  useEffect(() => {
    return () => { scannerRef.current?.stop().catch(() => {}); };
  }, []);

  return (
    <div className="overflow-hidden rounded-3xl">
      {started ? (
        <div id="reader" className="w-full" />
      ) : (
        <button
          type="button"
          onClick={startCamera}
          className="w-full flex flex-col items-center justify-center bg-zinc-900 cursor-pointer gap-3 min-h-[220px] border-0 rounded-b-3xl"
        >
          {error
            ? <p className="text-red-400 text-sm text-center px-6">{error}</p>
            : <>
                <QrCode className="w-12 h-12 text-white opacity-80" />
                <p className="text-white font-semibold text-sm">Tap to open camera</p>
              </>
          }
        </button>
      )}
    </div>
  );
}
